// src/schemas/registration.ts
import { z } from 'zod';
import { HERO_VIDEO_OPTIONS, VISION_IMAGE_OPTIONS } from '../types/registration';

/**
 * バリデーションスキーマ
 */

// URLバリデーション（任意）
const optionalUrlSchema = z
  .string()
  .trim()
  .refine(
    (val) => {
      if (!val) return true; // 空文字列はOK
      try {
        new URL(val);
        return true;
      } catch {
        return false;
      }
    },
    { message: '正しいURL形式で入力してください' }
  )
  .optional()
  .or(z.literal(''));

// URLバリデーション（必須）
const requiredUrlSchema = z
  .string({ message: 'URLを入力してください' })
  .trim()
  .min(1, 'プロフィール画像をアップロードしてください')
  .refine(
    (val) => {
      try {
        new URL(val);
        return true;
      } catch {
        return false;
      }
    },
    { message: '正しいURL形式で入力してください' }
  );

// Step 1: 基本情報
export const basicInfoSchema = z.object({
  agencyId: z
    .string({ message: '代理店IDを入力してください' })
    .trim()
    .regex(/^\d{10}$/, '代理店IDは半角数字10桁で入力してください'),
  agencyName: z
    .string({ message: '名前を入力してください' })
    .trim()
    .min(1, '名前を入力してください')
    .max(100, '名前は100文字以内で入力してください'),
  email: z
    .string({ message: 'メールアドレスを入力してください' })
    .trim()
    .min(1, 'メールアドレスを入力してください')
    .email('正しいメールアドレス形式で入力してください'),
  phone: z
    .string({ message: '電話番号を入力してください' })
    .trim()
    .regex(/^(\d{2,4}-\d{2,4}-\d{4})?$/, '電話番号は「000-0000-0000」の形式で入力してください')
    .optional()
    .or(z.literal('')),
});

// Step 2: LPカスタマイズ
export const lpCustomizeSchema = z.object({
  heroVideoKey: z.enum(HERO_VIDEO_OPTIONS.map((option) => option.value) as [string, ...string[]], {
    message: 'ヒーロー動画を選択してください',
  }),
  heroTitle: z
    .string({ message: 'ヒーロータイトルを入力してください' })
    .trim()
    .min(1, 'ヒーロータイトルを入力してください')
    .max(20, 'ヒーロータイトルは20文字以内で入力してください'),
  visionImageKey: z.enum(VISION_IMAGE_OPTIONS.map((option) => option.value) as [string, ...string[]], {
    message: 'ビジョン画像を選択してください',
  }),
});

// Step 3: プロフィール
export const profileInfoSchema = z.object({
  name: z
    .string({ message: '名前を入力してください' })
    .trim()
    .min(1, '名前を入力してください')
    .max(50, '名前は50文字以内で入力してください'),
  bio: z
    .string({ message: '紹介文を入力してください' })
    .trim()
    .min(1, '紹介文を入力してください')
    .max(1000, '紹介文は1000文字以内で入力してください'),
  photoUrl: requiredUrlSchema,
  socialLinks: z.object({
    instagram: optionalUrlSchema,
    twitter: optionalUrlSchema,
    youtube: optionalUrlSchema,
  }),
});

// Step 4: ブランディング
export const brandingInfoSchema = z.object({
  lineUrl: optionalUrlSchema,
  applicationUrl: optionalUrlSchema,
});

// 全体のスキーマ
export const registrationFormSchema = z.object({
  basicInfo: basicInfoSchema,
  lpCustomize: lpCustomizeSchema,
  profile: profileInfoSchema,
  branding: brandingInfoSchema,
});

export type RegistrationFormSchema = z.infer<typeof registrationFormSchema>;
